import queryString from 'query-string'
import {
  Alert,
  Descriptions,
  Divider,
  Form,
  Input,
  Image,
  List,
  Select,
  TextField,
  message,
  Collapse,
  Button,
  Icons,
} from '@pankod/refine-antd'
import React from 'react'
import Head from 'next/head'

import { generateLTIXML } from '@resources/ops-toolkit/generate-lti-config/utils'

import type { MenuConfigList } from 'apis/generate-config'

const LTITypeOptions: { value: keyof MenuConfigList; label: string }[] = [
  { label: 'Asisten Guru', value: 'asisten-guru' },
  { label: 'Asisten Guru (Pembelajaran Mendalam)', value: 'asisten-guru-pm' },
  { label: 'Forms', value: 'forms' },
  { label: 'Asisten', value: 'pengajar-praktik' },
  { label: 'Penilaian Kinerja Dasus', value: 'penilaian-kinerja-dasus' },
  { label: 'Penilaian Kinerja', value: 'penilaian-kinerja' },
  { label: 'Prasyarat Sertifikat', value: 'bukti-karya' },
  { label: 'Presensi', value: 'presensi' },
  { label: 'Timespend', value: 'timespend' },
  { label: 'Umpan Balik', value: 'umpan-balik' },
  { label: 'Validasi Prasyarat Sertifikat', value: 'penilaian-bukti-karya' },
  { label: 'Webinar', value: 'webinar' },
]

export const GenerateLTIConfigList = () => {
  const XMLRef = React.useRef<HTMLTextAreaElement>(null)
  const [form] = Form.useForm()
  const title = Form.useWatch('title', form)
  const type = Form.useWatch('type', form)
  const visibility = Form.useWatch('visibility', form)
  const description = Form.useWatch('description', form)
  const id = Form.useWatch('id', form)
  const privacyLevel = Form.useWatch('privacyLevel', form)

  const getConfigXML = () => {
    return generateLTIXML({
      courseNavigationText: title,
      courseNavigationVisibility: visibility,
      type,
      title,
      description,
      id,
      privacyLevel,
    })
  }

  return (
    <List title="Generate LTI Configuration">
      <Head>
        <title>LMS Admin | Generate LTI Configuration</title>
      </Head>
      <div className="grid grid-cols-2 gap-4">
        <section>
          <Divider orientation="left">
            Panduan Pengisian Konfigurasi LTI
          </Divider>
          <Collapse>
            <Collapse.Panel
              header="1. Buka halaman sub-account settings di Canvas LMS."
              key="1"
            >
              <Image
                alt="contoh sub-account settings"
                src="/img/static/contoh-subaccount-settings.png"
                className="rounded-lg"
              />
            </Collapse.Panel>
            <Collapse.Panel
              header={
                <>
                  2. Klik tombol <strong>+App</strong> untuk menambahkan
                  aplikasi baru.
                </>
              }
              key="2"
            >
              <Image
                alt="contoh klik add app"
                src="/img/static/contoh-klik-add-app.png"
                className="rounded-lg"
              />
            </Collapse.Panel>
            <Collapse.Panel
              header={
                <>
                  3. Ganti <strong>Configuration Type</strong> dari{' '}
                  <em>Manual Entry</em> menjadi <em>Paste XML</em>.
                </>
              }
              key="3"
            >
              <Image
                alt="contoh ubah config type"
                src="/img/static/contoh-ubah-config-type.png"
                className="rounded-lg"
              />
            </Collapse.Panel>
            <Collapse.Panel
              header={
                <>
                  4. Isi field yang tersedia dengan informasi yang telah
                  dikonfigurasi menggunakan konfigurasi di samping.
                </>
              }
              key="4"
            >
              <Image
                alt="contoh pengisian konfigurasi LTI"
                src="/img/static/contoh-pengisian-config-lti.png"
                className="rounded-lg"
              />
            </Collapse.Panel>
          </Collapse>
        </section>
        <section>
          <Divider orientation="left">Form Konfigurasi LTI</Divider>
          <Alert
            showIcon
            message="Isi field berikut ini sesuai dengan informasi LTI yang ingin dibuat"
            className="!mb-2"
          />
          <Form
            form={form}
            layout="vertical"
            onFinish={async (values) => {
              const { type, title, visibility, description } = values
              const searchParams = queryString.stringify({
                title,
                courseNavigationText: title,
                courseNavigationVisibility: visibility,
                description,
              })
              const configURL =
                new URL(
                  `${process.env.NEXT_PUBLIC_CANVAS_LTI_APP_URL}/api/generate-config/${type}`,
                ) + `?${searchParams}`

              await navigator.clipboard.writeText(configURL)
              message.success(
                'Config URL telah disalin ke clipboard. Silakan tempelkan di field Config URL pada Canvas LMS.',
              )
            }}
          >
            <Form.Item
              label="Jenis LTI"
              required
              rules={[{ required: true }]}
              name="type"
              getValueFromEvent={(data: { label: string; value: string }) => {
                form.setFieldsValue({ title: `LTI ${data?.label}` })
                return data?.value
              }}
              getValueProps={(data: { label: string; value: string }) => ({
                value: data?.value,
              })}
            >
              <Select
                labelInValue
                placeholder="Pilih jenis LTI"
                options={LTITypeOptions}
              />
            </Form.Item>
            <Form.Item
              label="Judul LTI"
              required
              rules={[{ required: true }]}
              name="title"
            >
              <Input placeholder="Masukkan judul LTI" />
            </Form.Item>
            <Form.Item
              name="visibility"
              label="Visibilitas (opsional)"
              initialValue={'public'}
            >
              <Select
                placeholder="Pilih visibilitas"
                options={[
                  {
                    label: 'Public',
                    value: 'public',
                  },
                  {
                    label: 'Members',
                    value: 'members',
                  },
                  {
                    label: 'Admins',
                    value: 'admins',
                  },
                ]}
              />
            </Form.Item>
            <Form.Item label="Deskripsi (opsional)" name="description">
              <Input.TextArea placeholder="Masukkan deskripsi" />
            </Form.Item>
            {type === 'forms' && (
              <Form.Item label="ID" name="id">
                <Input placeholder="Masukkan ID Form" />
              </Form.Item>
            )}
          </Form>
          <Divider orientation="left">Informasi Konfigurasi LTI</Divider>
          <Alert
            showIcon
            message="Salin dan masukkan informasi berikut ini sesuai dengan panduan di samping"
            className="!mb-2"
          />
          <Descriptions
            labelStyle={{ width: 200 }}
            bordered
            column={1}
            size="small"
          >
            <Descriptions.Item label="Name">
              <TextField
                value={title || '-'}
                copyable={
                  !!title
                    ? {
                        onCopy: () => {
                          message.success('Name telah disalin ke clipboard')
                        },
                      }
                    : false
                }
              />
            </Descriptions.Item>
            <Descriptions.Item label="Consumer Key">
              <TextField
                value="test"
                copyable={{
                  onCopy: () => {
                    message.success('Consumer Key telah disalin ke clipboard')
                  },
                }}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Shared Secret">
              <TextField
                value={process.env.NEXT_PUBLIC_CANVAS_LTI_SECRET}
                copyable={{
                  onCopy: () => {
                    message.success('Shared Secret telah disalin ke clipboard')
                  },
                }}
              />
            </Descriptions.Item>
            <Descriptions.Item label="XML Configuration">
              <Input.TextArea
                className="font-mono resize-none"
                rows={3}
                value={type ? getConfigXML().trim() : '-'}
                readOnly
                ref={XMLRef}
              />
              <Button
                size="small"
                className="mt-2"
                type="primary"
                icon={<Icons.CopyOutlined />}
                disabled={!type}
                onClick={async () => {
                  if (!XMLRef.current) return
                  await navigator.clipboard.writeText(getConfigXML().trim())
                  message.success('XML Configuration berhasil disalin!')
                }}
              >
                Salin XML
              </Button>
            </Descriptions.Item>
          </Descriptions>
        </section>
      </div>
    </List>
  )
}
